import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mzc-pdc/ui";
import { IconWarningTriangle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Typography from "@components_v2/typography/typography";

/**
 * 공통 경고/확인 모달 (Figma: cp-dialogs / style-01).
 *
 * `타이틀` + `(경고 아이콘 + 안내 메시지)` + `취소 / 확정` 버튼으로 구성된 표준 경고 다이얼로그.
 * 저장·교체 등 되돌릴 수 없는 액션 실행 전 확인용으로 사용한다. 텍스트/버튼 라벨만 다른
 * 여러 안내 팝업(커스텀 메타데이터 저장, 프리셋 스키마 교체 등)을 이 컴포넌트 하나로 커버한다.
 *
 * @param {object} props
 * @param {boolean} props.open 열림 여부
 * @param {React.ReactNode} props.title 타이틀
 * @param {React.ReactNode} props.message 안내 메시지(문자열이면 `\n` 줄바꿈 지원)
 * @param {React.ReactNode} props.confirmLabel 확정 버튼 라벨
 * @param {React.ReactNode} [props.cancelLabel] 취소 버튼 라벨(기본: "취소")
 * @param {() => void} props.onConfirm 확정 핸들러
 * @param {() => void} props.onClose 취소/닫기 핸들러
 * @param {string} [props.confirmColor="primary"] 확정 버튼 color (예: "primary", "error")
 * @param {boolean} [props.loading=false] 확정 버튼 로딩 상태
 * @param {boolean} [props.confirmDisabled=false] 확정 버튼 비활성
 * @param {boolean} [props.hideIcon=false] 경고 아이콘 숨김
 */
const DialogWarningConfirm = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onClose,
    confirmColor = "primary",
    loading = false,
    confirmDisabled = false,
    hideIcon = false,
}) => {
    const { t } = useTranslation();

    return (
        <Dialog
            variant={"basic"}
            open={open}
            onClose={onClose}
            sx={{ ".MuiPaper-root": { width: "480px", minWidth: "480px", maxWidth: "600px" } }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack direction={"row"} alignItems={"flex-start"} gap={1}>
                    {!hideIcon && (
                        <Box sx={{ display: "inline-flex", flexShrink: 0 }}>
                            <IconWarningTriangle size={20} htmlColor={theme.palette.warning.main} />
                        </Box>
                    )}
                    <Typography
                        variant={"body1_400"}
                        color={theme.palette.greySecondary.main}
                        sx={{ flex: 1, minWidth: 0, whiteSpace: "pre-line" }}
                    >
                        {message}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant={"text"} color={"greySecondary"} onClick={onClose}>
                    {cancelLabel ?? t(`common::label::Cancel`, `Cancel`)}
                </Button>
                <Button
                    variant={"contained"}
                    color={confirmColor}
                    loading={loading}
                    disabled={confirmDisabled || loading}
                    onClick={onConfirm}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

DialogWarningConfirm.propTypes = {
    open: PropTypes.bool,
    title: PropTypes.node,
    message: PropTypes.node,
    confirmLabel: PropTypes.node,
    cancelLabel: PropTypes.node,
    onConfirm: PropTypes.func,
    onClose: PropTypes.func,
    confirmColor: PropTypes.string,
    loading: PropTypes.bool,
    confirmDisabled: PropTypes.bool,
    hideIcon: PropTypes.bool,
};

export default DialogWarningConfirm;
